<template>
  <div class="relative flex border">
    <div class="flex items-center w-[150px] flex-0 bg-surface-100 px-4 py-2">
      <Button
        v-if="!isProccess && props.linkTo"
        :label="labelName"
        size="small"
        variant="outlined"
        severity="secondary"
        :pt-options="{
          variant: 'extra'
        }"
        as="a"
        :href="props.linkTo"
        target="_blank"
      />
    </div>

    <div class="w-[calc(100% - 150px)] flex-1 px-4 py-2">
      <div class="relative">
        <div
          v-if="props.pending"
          class="py-4"
        >
          <div class="relative w-full h-1 bg-gray-200 rounded overflow-hidden">
            <div class="absolute top-0 left-0 h-full bg-blue-500 animate-indeterminate"></div>
          </div>
          <div class="mt-2 text-sm text-red-500">
            {{ $t('uploadVideoFilePlaceholder') }}
          </div>
        </div>

        <span
          v-else-if="props.error"
          class="text-sm text-red-500"
        >
          {{ props.error }}
        </span>

        <template v-else>
          <div
            class="relative w-44 cursor-pointer px-3.5
              after:content-[''] after:bg-video-mask after:bg-cover after:h-full after:w-4 after:block after:absolute after:top-0 after:right-0 after:bg-right
              before:content-[''] before:bg-video-mask before:bg-cover before:h-full before:w-4 before:block before:absolute before:top-0 before:left-0 before:bg-left"
          >
            <div
              v-if="!isVideoUploaded || isUpdatedInfo"
              class="relative"
            >
              <NuxtPicture
                :img-attrs="{class: '', alt: props.data.title}"
                :src="props.data?.preview_image ? props.data?.preview_image : '/img/na.png'"
              />
              
              <div
                class="loading-indicator-overlay"
              ></div>
            </div>

            <div
              v-else
              class="relative"
            >
              <NuxtPicture
                :img-attrs="{class: 'w-full h-full object-cover', alt: props.data.title}"
                :src="props.data.preview_image"
              />
            </div>
          </div>

          <Message
            v-if="isProccess && !isUpdatedInfo"
            class="mt-2"
            severity="warn"
          >
            {{ $t('greetingVideoProccessing') }}
          </Message>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
const { $i18n: i18n} = useNuxtApp()
const { getVimeoInfo } = useGeneral()
const { isVideoUploaded: checkVideoUploaded, isVideoReady } = useVimeoProcessing()
const toast = useToast()

/**
* Props
*/
const props = defineProps({
  data: {
    type: [Object],
    default: null
  },
  linkTo: {
    type: [String],
    default: null
  },
  linkType: {
    type: [String],
    default: null
  },
  labelName: {
    type: [String],
    default: null
  },
  error: {
    type: [Boolean],
    default: false
  },
  pending: {
    type: [Boolean],
    default: false
  }
})

/**
* Emits
*/
const emit = defineEmits(['returnData'])

/**
* Video in proccess (for spinner and polling)?
*/
const isProccess = computed({
  get() {
    // Keep polling/spinner logic: continue until video is ready (has proper thumbnails)
    return !isVideoReady(props.data)
  }
})

/**
* Video uploaded (for hiding loader overlay)?
*/
const isVideoUploaded = computed({
  get() {
    // Hide the loading overlay when video is uploaded (has URLs)
    return checkVideoUploaded(props.data)
  }
})

/**
* Video in updated info?
*/
const isUpdatedInfo = computed({
  get() {
    return props.data && props.data?.url && !props.data?.preview_image
  }
})

/**
* Check Video Info
*/
let tick = ''
let vimeoInfoTick = ''
const checkVideoInfo = () => { 
  clearTimeout(tick)
  
  tick = setTimeout(() => {
    /**
    * Get Vimeo Info and update after video proccessing
    */
    if (isProccess.value || (props.data?.url && !props.data?.main_image)) {
      clearTimeout(vimeoInfoTick)
      vimeoInfoTick = setInterval(() => {
        getVimeoInfo({
          video_url: props.data.url
        }).then((res) => {
          if (res.data?.video_info) {
            const videoInfo = res.data.video_info
            
            // Check if video is ready (has proper thumbnails, not default placeholders)
            const hasProperThumbnails = videoInfo.main_image && 
              !videoInfo.main_image.includes('video/default') && 
              !videoInfo.main_image.includes('default-')
            
            if (hasProperThumbnails) {
              clearInterval(vimeoInfoTick)

              /**
              * Return Data after update preview
              */
              emit('returnData', videoInfo)

              toast.add({
                severity: 'success',
                detail: i18n?.t('greetingVideoProccessingSuccess'),
                life: 5000
              })
            }
          } else { 
            clearInterval(vimeoInfoTick)
          }
        }).catch((err) => { 
          if (err.error) { 
            clearInterval(vimeoInfoTick)
          }
        })
      }, 20000)
    } else {
      clearInterval(vimeoInfoTick)
    }
  }, 1000)
}

/**
* If it Greeting Video Preview
*/
watch(isProccess, async (newA) => {
  if (newA) {
    checkVideoInfo()
  }
}, { deep: true })

onMounted(() => {
  checkVideoInfo()
})

/**
* Update Video Info, if the video is uploaded but the information is empty
*/
if (isProccess.value || (props.data?.url && !props.data?.main_image)) { 
  getVimeoInfo({
    video_url: props.data.url
  }).then((res) => {
    emit('returnData', res.data.video_info)
  })
}
</script>

